import React from 'react';

const CATEGORY_NAMES = {
  sweets: 'Sweets',
  bakery: 'Bakery',
  restaurant: 'Restaurant',
  fmcg: 'FMCG',
};

const CATEGORY_ITEMS = {
  sweets: 353, bakery: 60, restaurant: 245, fmcg: 288,
};

const TOOL_TYPE_LABELS = {
  sweet_category: 'Sweet Category',
  restaurant_item: 'Restaurant Item',
  rm_pm: 'RM / PM',
};

function SummaryCard({ title, children, className = '' }) {
  return (
    <div className={`rounded-2xl border border-gray-200 bg-white p-5 shadow-sm ${className}`}>
      <h4 className="mb-4 text-sm font-semibold text-gray-900">{title}</h4>
      {children}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-gray-100 py-2 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="max-w-[60%] text-right text-sm font-semibold text-gray-900">
        {value || '—'}
      </span>
    </div>
  );
}

function StepReviewConfirm({ wizardData }) {
  const params = wizardData.parameters || {};
  const categories = wizardData.categories || [];
  const totalItems = categories.reduce((sum, id) => sum + (CATEGORY_ITEMS[id] || 0), 0);

  const salesHistoryLabel = {
    '3': '3 Weeks (W1, W2, W3)',
    '4': '4 Weeks (W1, W2, W3, W4)',
    '6': '6 Weeks',
    '8': '8 Weeks',
  }[params.salesHistoryWeeks] || params.salesHistoryWeeks || '—';

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-brand/10 bg-[#FFF7F0] p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Step 4: Review &amp; Confirm</h3>
            <p className="mt-1 text-sm text-gray-500">
              Please review your selections and configurations before creating the tool.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Tool Type</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">
                {TOOL_TYPE_LABELS[wizardData.toolType] || '—'}
              </p>
            </div>
            <div className="rounded-xl bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Date</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">
                {wizardData.toolDate || '—'}
              </p>
            </div>
            <div className="rounded-xl bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Categories</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">{categories.length}</p>
            </div>
            <div className="rounded-xl bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Items</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">{totalItems}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <SummaryCard title="Tool Summary">
          <SummaryRow label="Tool Type" value={TOOL_TYPE_LABELS[wizardData.toolType]} />
          <SummaryRow label="Tool Name" value={wizardData.toolName} />
          <SummaryRow label="Tool Code" value={wizardData.toolCode} />
          <SummaryRow label="Date" value={wizardData.toolDate || '—'} />
          <SummaryRow label="Description" value={wizardData.toolDescription || '—'} />
        </SummaryCard>

        <SummaryCard title="Outlet">
          <SummaryRow label="Outlet Name" value="Arya Samaj Road" />
          <SummaryRow label="Outlet Code" value="OUT001" />
          <SummaryRow label="Location" value="Delhi" />
        </SummaryCard>

        <SummaryCard title={`Categories (${categories.length})`}>
          <div className="flex flex-wrap gap-2">
            {categories.length > 0 ? (
              <>
                {categories.slice(0, 5).map((id) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1.5 rounded-full border border-brand/10 bg-[#FFF7F0] px-3 py-1 text-xs font-medium text-gray-700"
                  >
                    <svg className="h-3.5 w-3.5 text-brand" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {CATEGORY_NAMES[id] || id}
                  </span>
                ))}
                {categories.length > 5 && (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500">
                    + {categories.length - 5} more
                  </span>
                )}
              </>
            ) : (
              <span className="text-xs text-gray-400">No categories selected</span>
            )}
          </div>
        </SummaryCard>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <SummaryCard title="Parameters">
          <SummaryRow label="Lead Time (Days)" value={params.leadTimeDays} />
          <SummaryRow label="Buffer %" value={params.bufferPercent ? `${params.bufferPercent}%` : '—'} />
          <SummaryRow label="Projected Growth %" value={params.projectedGrowth ? `${params.projectedGrowth}%` : '—'} />
          <SummaryRow label="Sales History (Weeks)" value={salesHistoryLabel} />
        </SummaryCard>

        <SummaryCard title="Inventory Consideration">
          {[
            ['includeClosingStock', 'Include Closing Stock'],
            ['includeIssuedStock', 'Include Issued Stock'],
            ['includeProductionStock', 'Include Production Stock'],
            ['includeDamagedStock', 'Include Damaged Stock'],
            ['includeReturnsStock', 'Include Returns Stock'],
          ].map(([key, label]) => (
            <div
              key={key}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 ${
                params[key] ? 'bg-emerald-50' : 'bg-red-50'
              }`}
            >
              {params[key] ? (
                <svg className="h-4 w-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <svg className="h-4 w-4 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              )}
              <span className="text-sm text-gray-700">{label}</span>
            </div>
          ))}
        </SummaryCard>

        <SummaryCard title="Items">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
            <div className="rounded-xl bg-gray-50 px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Total Categories
              </p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{categories.length}</p>
            </div>
            <div className="rounded-xl bg-gray-50 px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Total Items
              </p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{totalItems}</p>
            </div>
          </div>
        </SummaryCard>
      </div>
    </div>
  );
}

export default StepReviewConfirm;
